// Packages
import React from "react";
import PropTypes from "prop-types";
import { FormGroup, FormControlLabel, Checkbox } from "@mui/material";
import styled from "styled-components";
import tw from "twin.macro";

import useControllers from "../../../controllers";
import { StyledHelperText } from "../SelectField/SelectField.styles";

const StyledFormGroup = styled(FormGroup)`
  ${tw`mb-2`}
  break-inside: avoid;
`;

const StyledContainer = styled.div`
  column-count: 2;
  column-gap: 2rem;

  @media (max-width: 768px) {
    column-count: 1;
  }
`;

const StyledParentLabel = styled(FormControlLabel)`
  ${tw`font-medium`}
  .MuiTypography-root {
    ${tw`text-sm text-neutral font-medium`}
  }
`;

const StyledChildLabel = styled(FormControlLabel)`
  ${tw`ml-8`}
  .MuiTypography-root {
    ${tw`text-sm text-neutral font-light`}
  }
`;

const StyledCheckbox = styled(Checkbox)`
  &.MuiCheckbox-root {
    color: #FFB62A;
  }
  &.Mui-checked {
    color: #FFB62A;
  }
  &.MuiCheckbox-indeterminate {
    color: #FFB62A;
  }
`;

const HierarchicalPermissionsGroup = (props) => {
  const { groupedPermissions, control, name, className } = props;

  const { useComponentHooks } = useControllers();
  const { useHierarchicalPermissions } = useComponentHooks();

  const {
    value,
    ref,
    error,
    handleParentChange,
    handleChildChange,
    getParentState,
    isChildChecked,
  } = useHierarchicalPermissions(control, name);

  return (
    <div className={className}>
      <StyledContainer>
        {groupedPermissions.map((group, groupIndex) => {
          // Check if this is a standalone permission (no children)
          const isStandalone = group.children.length === 0;

          if (isStandalone) {
            // Render as a simple checkbox
            return (
              <StyledFormGroup key={`permission-group-${groupIndex}`}>
                <StyledParentLabel
                  control={
                    <StyledCheckbox
                      checked={isChildChecked(group.parent.value)}
                      onChange={(e) => handleChildChange(e, group.parent.value, null, [])}
                      inputRef={ref}
                    />
                  }
                  label={group.parent.label}
                />
              </StyledFormGroup>
            );
          }

          // Render parent with children
          const parentState = getParentState(group.children);

          return (
            <StyledFormGroup key={`permission-group-${groupIndex}`}>
              {/* Parent Permission */}
              <StyledParentLabel
                control={
                  <StyledCheckbox
                    checked={parentState.checked}
                    indeterminate={parentState.indeterminate}
                    onChange={(e) => handleParentChange(e, group.parent, group.children)}
                    inputRef={ref}
                  />
                }
                label={group.parent.label}
              />

              {/* Children Permissions */}
              {group.children.map((child, childIndex) => (
                <StyledChildLabel
                  key={`child-${childIndex}`}
                  control={
                    <StyledCheckbox
                      checked={isChildChecked(child.value)}
                      onChange={(e) => handleChildChange(e, child.value, group.parent.value, group.children)}
                      inputRef={ref}
                    />
                  }
                  label={child.label}
                />
              ))}
            </StyledFormGroup>
          );
        })}
      </StyledContainer>
      {error && <StyledHelperText>{error?.message}</StyledHelperText>}
    </div>
  );
};

HierarchicalPermissionsGroup.propTypes = {
  groupedPermissions: PropTypes.arrayOf(
    PropTypes.shape({
      parent: PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
      }),
      children: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          value: PropTypes.number.isRequired,
        })
      ),
    })
  ).isRequired,
  control: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
};

export default HierarchicalPermissionsGroup;
